import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Star, Calendar, User } from 'lucide-react';
import type { FrameworkStory } from '../../types/collaboration';

interface FrameworkStoryCardProps {
  story: FrameworkStory;
  onClick?: () => void;
}

export const FrameworkStoryCard: React.FC<FrameworkStoryCardProps> = ({ story, onClick }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div
      onClick={onClick}
      className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:shadow-lg transition-all cursor-pointer group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          {story.featured && (
            <div className="flex items-center gap-1 text-[var(--mustard)] mb-2">
              <Star className="h-4 w-4 fill-current" />
              <span className="text-xs font-medium">Featured Story</span>
            </div>
          )}
          <h3 className="text-xl font-bold text-charcoal-900 mb-2 group-hover:text-primary-700 transition-colors">
            {story.title}
          </h3>
          <Link
            to={`/elevation/entry/${story.frameworkId}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-block px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium hover:bg-primary-100 transition-colors"
          >
            {story.frameworkName}
          </Link>
        </div>
      </div>

      {/* Description */}
      <p className="text-charcoal-700 mb-4 line-clamp-2">
        {story.description}
      </p>

      {/* Situation & Outcome Preview */}
      <div className="space-y-3 mb-4">
        <div className="bg-coral-50 border-l-4 border-coral-400 p-3 rounded">
          <p className="text-sm font-semibold text-coral-900 mb-1">Challenge</p>
          <p className="text-sm text-coral-800 line-clamp-2">{story.situation}</p>
        </div>
        <div className="bg-sage-50 border-l-4 border-sage-400 p-3 rounded">
          <p className="text-sm font-semibold text-sage-900 mb-1">Outcome</p>
          <p className="text-sm text-sage-800 line-clamp-2">{story.outcome}</p>
        </div>
      </div>

      {/* Tags */}
      {story.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {story.tags.map((tag, idx) => (
            <span
              key={idx}
              className="px-2 py-1 bg-[var(--hover-tint)] text-charcoal-700 text-xs rounded"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-[var(--rule)]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
            <User className="h-4 w-4 text-primary-700" />
          </div>
          <div>
            <p className="text-sm font-medium text-charcoal-900">{story.author.name}</p>
            <p className="text-xs text-charcoal-500">{story.author.role}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4 text-sm text-charcoal-600">
          <div className="flex items-center gap-1">
            <Heart className="h-4 w-4" />
            <span>{story.likes}</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageCircle className="h-4 w-4" />
            <span>{story.commentCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span className="text-xs">{formatDate(story.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

